import React, { useEffect, useState } from 'react'
import { Navigate, useLocation, Outlet } from 'react-router-dom'
import { ADMIN_REFRESH_TOKEN_KEY, isAdminAuthenticated } from '../../auth/adminAuth'
import { refreshAdminAccessToken } from '../../api/authenticatedFetch'
import { BookingsProvider } from '../../context/BookingsContext'

type AdminProtectedRouteProps = {
  children?: React.ReactNode
}

/**
 * Route protection component for the Salzburg Cab management dashboard.
 * Prevents unauthorized access without a valid admin login session.
 * Redirects unauthenticated users immediately to /admin-login.
 */
export const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({ children }) => {
  const location = useLocation()
  const [checking, setChecking] = useState(true)
  const [authenticated, setAuthenticated] = useState(false)

  useEffect(() => {
    let mounted = true

    const validateSession = async () => {
      if (isAdminAuthenticated()) {
        if (mounted) {
          setAuthenticated(true)
          setChecking(false)
        }
        return
      }

      if (localStorage.getItem(ADMIN_REFRESH_TOKEN_KEY)) {
        const refreshed = await refreshAdminAccessToken()
        if (mounted) {
          setAuthenticated(Boolean(refreshed))
          setChecking(false)
        }
        return
      }

      if (mounted) setChecking(false)
    }

    void validateSession()
    return () => {
      mounted = false
    }
  }, [])

  if (checking) return null

  if (!authenticated) {
    // Preserve the attempted location so admin can be returned after login
    return <Navigate to="/admin-login" replace state={{ from: location }} />
  }

    return (
    <BookingsProvider>
      {children ? children : <Outlet />}
    </BookingsProvider>
  )
}

export default AdminProtectedRoute
